import { useEffect, useMemo, useState } from "react";
import { GetFaQsByEventQuery } from "@src/@types/api.d";

type UseFaqListOptions = {
  faqData?: GetFaQsByEventQuery;
  itemsPerPage?: number;
  wrapPagination?: boolean;
  resetOpenOnPageChange?: boolean;
};

export type FaQByEvent = GetFaQsByEventQuery["getFAQsByEvent"]["data"][number];

export const useFaqList = ({
  faqData,
  itemsPerPage = 3,
  wrapPagination = false,
  resetOpenOnPageChange = false,
}: UseFaqListOptions) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqItems = useMemo(
    () =>
      (faqData?.getFAQsByEvent?.data || []).filter(
        (item): item is FaQByEvent =>
          item !== null && !!item.question && !!item.answer && item.is_active
      ),
    [faqData]
  );

  const sortedFaqItems = useMemo(() => {
    return [...faqItems].sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return dateA - dateB;
    });
  }, [faqItems]);

  const totalPages = Math.ceil(sortedFaqItems.length / itemsPerPage);

  const normalizePage = (page: number) => {
    if (totalPages <= 0) return 1;

    if (wrapPagination) {
      if (page < 1) return totalPages;
      if (page > totalPages) return 1;
      return page;
    }

    return Math.min(totalPages, Math.max(1, page));
  };

  useEffect(() => {
    if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
      return;
    }

    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    setCurrentPage(normalizePage(page));
    if (resetOpenOnPageChange) {
      setOpenIndex(null);
    }
  };

  const goToPreviousPage = () => {
    handlePageChange(currentPage - 1);
  };

  const goToNextPage = () => {
    handlePageChange(currentPage + 1);
  };

  const toggleFAQ = (index: number) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFaqItems = sortedFaqItems.slice(startIndex, endIndex);

  return {
    faqItems,
    sortedFaqItems,
    paginatedFaqItems,
    currentPage,
    totalPages,
    openIndex,
    toggleFAQ,
    handlePageChange,
    goToPreviousPage,
    goToNextPage,
  };
};
